import React, { useState, useEffect, useCallback } from 'react'
import cn from 'classnames'
import useStore from 'state/knovStore'
import FilterOptions from 'components/filters/FilterOptions'
import styles from './left-sidebar.module.scss'
import { isMobile } from 'lib/device'
import VersionInfo from 'components/shared/VersionInfo'
import Sidebar from 'components/shared/Sidebar/Sidebar'
import SidebarButton from 'components/shared/SidebarButton'
import WithTooltip from 'components/shared/WithTooltip'
import TDP from 'components/shared/TDP'
import BsvUsdSwitch from 'components/shared/BsvUsdSwitch'
import ThemeToggleButton from 'components/shared/ThemeToggleButton'
import PanelToggleButton from 'components/panels/PanelToggleButton'
import { Info, LogOut } from 'lucide-react'
import { resolveEffectiveTheme } from 'state/knovStore'
import useUserHasFeature from 'refactor/hooks/useUserHasFeature'
import { homePrivateEnabled } from 'lib/homePrivate'
import { treechatRadioEnabled } from 'lib/treechatRadio'
import { clearAccessTokens } from 'api/accessTokenMgmt'
import { redirectToLoginScreen } from '../../../AppRoot'
import NewQuestButton from 'components/quests/NewQuestButton'
import NewQuestButtonUIMainWrapper from 'components/quests/NewQuestButtonUIMainWrapper'
import useStreamFilters from 'components/filters/useStreamFilters'

/**
 * @typedef {{
 *   showSidebar?: boolean,
 *   closeOnSwipe?: boolean,
 *   onePanel?: boolean,
 *   toggleOnePanel?: () => void,
 *   toggleSidebar?: () => void,
 *   [key: string]: unknown,
 * }} LeftSidebarProps
 */

export default React.forwardRef(function LeftSidebar(/** @type {LeftSidebarProps} */ props, ref) {
    const { showSidebar, closeOnSwipe = false, onePanel, toggleOnePanel } = props
    const isMobileDevice = isMobile
    const knovigatorSpace = useStore(state => state.knovigatorSpace)
    const activeSpaceId = useStore(state => state.activeSpaceId)
    const userSpaces = useStore(state => state.userSpaces)
    const theme = useStore(state => state.appearanceTheme)
    const setAppearanceTheme = useStore(state => state.setAppearanceTheme)

    // Track the effective theme (resolves 'system' to actual light/dark)
    const [effectiveTheme, setEffectiveTheme] = useState(() => resolveEffectiveTheme(theme))
    const setShowSidebar = useStore.getState().setShowSidebar
    const setShowRightSidebar = useStore.getState().setShowRightSidebar
    const setCloseSidebarOnSwipe = useStore.getState().setCloseSidebarOnSwipe
    const { filter: activeFilter } = useStreamFilters()

    // Use state to track if an update is available
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)

    // Check if window.Knov has an isUpdateAvailable property, update once on mount
    useEffect(() => {
        // This will sync with the global version check state
        const checkGlobalUpdateStatus = () => {
            if (window.Knov && typeof window.Knov.isUpdateAvailable === 'boolean') {
                setIsUpdateAvailable(window.Knov.isUpdateAvailable)
            }
        }

        // do an immediate check on mount to prevent race condition while app is loading
        checkGlobalUpdateStatus()

        // Set up event listener for version changes
        const handleVersionUpdate = event => {
            if (event.detail && typeof event.detail.isUpdateAvailable === 'boolean') {
                setIsUpdateAvailable(event.detail.isUpdateAvailable)
            } else {
                // If no detail is available, check the global state
                checkGlobalUpdateStatus()
            }
        }

        window.addEventListener('versionCheckUpdate', handleVersionUpdate)

        return () => {
            window.removeEventListener('versionCheckUpdate', handleVersionUpdate)
        }
    }, [])

    // Listen for system preference changes when in 'system' mode
    useEffect(() => {
        // Update effective theme when theme preference changes
        setEffectiveTheme(resolveEffectiveTheme(theme))

        // Only set up listeners if we're in system mode
        if (theme !== 'system') {
            return
        }

        const handleSystemThemeChange = () => {
            setEffectiveTheme(resolveEffectiveTheme(theme))
        }

        // Browser media query listener (for web)
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        mediaQuery.addEventListener('change', handleSystemThemeChange)

        // Native app theme change listener (for mobile app webview)
        window.addEventListener('nativeSystemThemeChange', handleSystemThemeChange)

        return () => {
            mediaQuery.removeEventListener('change', handleSystemThemeChange)
            window.removeEventListener('nativeSystemThemeChange', handleSystemThemeChange)
        }
    }, [theme])

    const [showVersionInfo, setShowVersionInfo] = useState(false)

    const handleShowAbout = () => setShowVersionInfo(true)

    const handleLogout = async () => {
        window.logEv?.('CLICK_LOGOUT', { location: 'left_sidebar' })
        clearAccessTokens()
        // @ts-expect-error savePath inferred as literal true due to default value shape
        redirectToLoginScreen({ savePath: false })
    }

    const isUserSignedIn = () =>
        typeof window !== 'undefined' ? Boolean(window.gon?.currentUser) : false
    const signedIn = isUserSignedIn()

    useEffect(() => {
        setCloseSidebarOnSwipe(closeOnSwipe)
        return () => setCloseSidebarOnSwipe(false)
    }, [closeOnSwipe, setCloseSidebarOnSwipe])

    const showClipsInSidebar = useUserHasFeature('show_clips_left_sidebar')
    const showRadioInSidebar = treechatRadioEnabled()

    let show = isMobile
        ? [
              'home',
              'private',
              'streams',
              'memes',
              'ordinals',
              'market',
              //'links',
              signedIn ? 'quest-history' : 'teams',
          ]
        : [
              'home',
              'private',
              'notifications',
              'streams',
              'memes',
              'search',
              'ordinals',
              'market',
              //'links',
              signedIn ? 'quest-history' : 'teams',
              'account',
          ]
    // home_private: Private lives as a scope tab on the Home
    // surface, so the dedicated sidebar entry goes away.
    if (homePrivateEnabled()) show = show.filter(key => key !== 'private')
    // Feature-flagged Clips system stream; renders below Search.
    if (showClipsInSidebar) show.push('clips')
    // Treechat Radio has its own public stream; it is not in Public/Following.
    if (showRadioInSidebar) show.push('radio')

    const sidebarThemeVariant = isMobileDevice ? `mobile-${effectiveTheme}` : effectiveTheme
    const userHasTdpFeatureEnabled = useUserHasFeature('treechat_domestic_product')

    // Cycle through: light -> dark -> system -> light
    const toggleTheme = () => {
        const nextTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'
        setAppearanceTheme(nextTheme)
    }

    const renderActions = ({ wrapperClassName, shouldShowPanelToggle, showUpdateIndicator }) => (
        <div className={wrapperClassName}>
            {shouldShowPanelToggle && (
                <PanelToggleButton
                    onePanel={onePanel}
                    toggleOnePanel={toggleOnePanel}
                    contextStyles={cn(styles.panelToggleSlot, styles.headerIconButton)}
                />
            )}
            <ThemeToggleButton
                theme={theme}
                effectiveTheme={effectiveTheme}
                onToggle={toggleTheme}
                className={styles.headerIconButton}
                iconSize={18}
            />

            <WithTooltip tip="About Treechat">
                <div
                    role="button"
                    className={cn(
                        styles.headerIconButton,
                        showUpdateIndicator && isUpdateAvailable && styles.updateAvailable,
                    )}
                    onClick={handleShowAbout}
                    aria-label="About Treechat"
                >
                    <Info size={18} />
                </div>
            </WithTooltip>

            {isUserSignedIn() && (
                <WithTooltip tip="Logout">
                    <div
                        role="button"
                        className={styles.headerIconButton}
                        onClick={handleLogout}
                        aria-label="Logout"
                    >
                        <LogOut size={18} />
                    </div>
                </WithTooltip>
            )}
        </div>
    )

    const renderHeaderActions = extraClassName =>
        renderActions({
            wrapperClassName: cn(styles.headerActions, extraClassName),
            shouldShowPanelToggle: isMobileDevice,
            showUpdateIndicator: true,
        })

    const renderFooterActions = () =>
        renderActions({
            wrapperClassName: styles.footerActions,
            shouldShowPanelToggle: !isMobileDevice,
            showUpdateIndicator: true,
        })

    const handleFilterClose = useCallback(() => {
        setShowRightSidebar(false)
        if (isMobileDevice) {
            setShowSidebar(false)
        }
    }, [isMobileDevice, setShowRightSidebar, setShowSidebar])

    const lightTheme = effectiveTheme === 'light'
    const footerNewQuestFilter = activeFilter || { private: true }
    // Use desktop light layout only when on desktop AND in light mode
    const useDesktopLightLayout = !isMobileDevice && lightTheme

    return (
        <>
            <Sidebar
                ref={ref}
                show={Boolean(showSidebar)}
                onClose={() => setShowSidebar(false)}
                showOverlay={isMobileDevice}
                closeOnOutsideClick={false}
                contentProps={
                    /** @type {React.HTMLAttributes<HTMLDivElement> & Record<string, unknown>} */ ({
                        id: 'sidebar',
                        'data-swipe-region': 'left-sidebar',
                        tabIndex: 0,
                        className: cn(
                            styles.content,
                            !isMobileDevice && styles.onePanelShell,
                            isMobileDevice && styles.mobileContent,
                            showSidebar && styles.showSidebar,
                        ),
                        'data-sidebar-theme': sidebarThemeVariant,
                    })
                }
            >
                <div className={styles.sidebarHeader}>
                    {showSidebar && (
                        <SidebarButton
                            showSidebar={showSidebar}
                            toggleSidebar={props.toggleSidebar}
                            customClass="sidebar-button"
                            testId="sidebar-toggle-button"
                        />
                    )}

                    <BsvUsdSwitch />

                    <TDP />

                    {isMobileDevice && renderHeaderActions(styles.mobileHeaderActions)}
                </div>

                <FilterOptions
                    user={gon.currentUser}
                    //header="Streams"
                    show={show}
                    theme={effectiveTheme}
                    contextStyles={cn(
                        styles.filterOptions,
                        !isMobileDevice && styles.onePanelFilterOptions,
                    )}
                    selectedStyle={
                        useDesktopLightLayout ? styles.onePanelSelected : styles.selected
                    }
                    hoverStyle={useDesktopLightLayout ? styles.onePanelHover : styles.hover}
                    blur={false}
                    //setShowFilterOptions={setShowFilterOptions}
                    close={handleFilterClose}
                    append={true}
                    disableTooltips={true}
                    allowGuestNewQuest
                    disableNewQuestTooltip
                />
                {!isMobileDevice && (
                    <div
                        className={cn(
                            styles.sidebarFooter,
                            lightTheme ? styles.lightFooter : styles.darkFooter,
                        )}
                    >
                        <div className={styles.footerPrimaryAction}>
                            <NewQuestButton
                                filter={footerNewQuestFilter}
                                callback={handleFilterClose}
                                place="top"
                                authSource="left_sidebar_join"
                                joinOnGuest
                                disableTooltip
                            >
                                <NewQuestButtonUIMainWrapper
                                    filter={footerNewQuestFilter}
                                    label={isUserSignedIn() ? 'New Thread' : 'Join'}
                                    showLabel
                                    showIcon={false}
                                    fullWidth
                                    contextStyles={styles.footerNewQuestButton}
                                />
                            </NewQuestButton>
                        </div>
                        <div className={styles.footerActionsContainer}>{renderFooterActions()}</div>
                    </div>
                )}
            </Sidebar>
            {showVersionInfo && (
                <VersionInfo
                    isUpdateAvailable={isUpdateAvailable}
                    onClose={() => setShowVersionInfo(false)}
                />
            )}
        </>
    )
})
